import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { ConfigurationAudit, GlobalConfiguration } from 'src/app/models/GlobalConfiguration';
import { PaginatedResult } from 'src/app/shared/model/paginated-result';
import {
  GlobalConfigurationService,
  isHistoryUnavailable,
} from 'src/app/shared/services/global-configuration.service';

/** Cabe no modal sem rolagem infinita e é o recorte útil num incidente. */
export const HISTORY_PER_PAGE = 10;

/**
 * Histórico de alteração de UMA chave (CU-86akkmfav).
 *
 * A trilha existe para reconstruir "por que os envios pararam ontem às 19h", e
 * isso só funciona se estiver ao lado do campo — no banco ela serve à auditoria
 * depois, não a quem está no meio do incidente.
 *
 * ## Valores crus
 *
 * `old_value` e `new_value` chegam como estão na coluna: texto normalizado pelo
 * backend, não o valor tipado do catálogo. Quem traduz para leitura é esta tela,
 * pelo `type` da chave que abriu o modal — é por isso que o modal recebe a
 * configuração inteira e não só a chave.
 *
 * ## Por que mora em `shared/`
 *
 * O modal nasceu dentro de `admin-configurations`, mas a tela de bônus de
 * recarga (CU-86akn0zp6) precisa do MESMO histórico para a chave
 * `WALLET_BONUS_TIERS`. Importar o `AdminConfigurationsModule` inteiro traria
 * junto o `RouterModule.forChild` dele, registrando as rotas das configurações
 * dentro do módulo de preços. Mover o modal é a mudança menor — e ele já só
 * dependia de `shared/`.
 *
 * ## Rota ausente
 *
 * A gravação da auditoria já existe; a leitura está subindo em paralelo. 404,
 * 405 e 501 viram um aviso dentro do painel, não um erro: a tela de configuração
 * continua inteira e utilizável sem o histórico.
 */
@Component({
  selector: 'app-configuration-history-modal',
  templateUrl: './configuration-history-modal.component.html',
  styleUrls: ['./configuration-history-modal.component.scss'],
})
export class ConfigurationHistoryModalComponent implements OnInit, OnDestroy {

  @Input() configuration: GlobalConfiguration;

  result: PaginatedResult<ConfigurationAudit[]> | null = null;

  /** Começa carregando: o "nenhuma alteração" não pode piscar antes da resposta. */
  isLoading = true;
  loadError: string | null = null;
  isUnavailable = false;

  readonly perPage = HISTORY_PER_PAGE;

  /**
   * A página pedida por último. É ela que o "Tentar de novo" repete: no erro o `result` zera
   * e o `currentPage` cairia na página 1.
   */
  requestedPage = 1;

  /** Uma requisição por vez: paginar cancela a anterior. */
  private subscription: Subscription | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private globalConfigurationService: GlobalConfigurationService,
  ) {}

  ngOnInit(): void {
    this.load(1);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get entries(): ConfigurationAudit[] {
    return this.result?.data || [];
  }

  get isEmpty(): boolean {
    return !this.isLoading && !this.isUnavailable && this.loadError === null && this.entries.length === 0;
  }

  get currentPage(): number {
    return this.result?.current_page || 1;
  }

  get totalItems(): number {
    return this.result?.total || 0;
  }

  load(page: number): void {
    // Antes de ligar o carregamento: o `finalize` também dispara no unsubscribe
    // e desligaria o esqueleto que acabamos de ligar.
    this.subscription?.unsubscribe();

    this.requestedPage = page;
    this.isLoading = true;
    this.loadError = null;
    this.isUnavailable = false;

    // `finalize` garante o fim do carregamento mesmo se o modal for fechado com
    // a requisição em voo — sem ele o esqueleto ficaria eterno na reabertura.
    this.subscription = this.globalConfigurationService
      .history(this.configuration.key, page, this.perPage)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => (this.result = result),
        error: (httpError) => {
          this.result = null;

          if (isHistoryUnavailable(httpError)) {
            this.isUnavailable = true;
            return;
          }

          this.loadError = 'Não foi possível carregar o histórico desta configuração.';
        },
      });
  }

  /** Micros do valor cru, para o pipe de dinheiro formatar. */
  asMicros(value: string | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const micros = Number(value);

    return isFinite(micros) ? micros : null;
  }

  /**
   * Valor cru da coluna traduzido pelo tipo da chave. `null` é a chave que ainda
   * não tinha linha — o sistema estava no default do catálogo, e dizer isso é
   * diferente de mostrar um traço.
   */
  describe(value: string | null): string {
    if (value === null || value === undefined) {
      return 'sem valor (padrão do código)';
    }

    if (value === '') {
      return 'vazio';
    }

    switch (this.configuration.type) {
      case 'boolean':
        return value === 'true' ? 'Ligado' : 'Desligado';

      default:
        return value;
    }
  }

  isMoney(): boolean {
    return this.configuration.type === 'money_micros';
  }

  isDatetime(): boolean {
    return this.configuration.type === 'datetime';
  }

  /**
   * Data só é data quando é um instante legível; `null`, vazio e texto ilegível continuam no
   * `describe`. O `DatePipe` lança com texto que não é data (`'2026-13-40 xx'`, que o backend
   * tolera na listagem e a auditoria pode ter gravado) e derrubaria o modal inteiro.
   */
  hasDatetime(value: string | null): boolean {
    return this.isDatetime() && !!value && !isNaN(new Date(value).getTime());
  }

  hasMoney(value: string | null): boolean {
    return this.isMoney() && this.asMicros(value) !== null;
  }

  trackByAuditId(_index: number, entry: ConfigurationAudit): string {
    return entry.id;
  }
}
